import type { Audience } from '@/types/types'

export function resolveIncludedAudiences<T extends Pick<Audience, 'id'>>(
  allAudiences: T[],
  selectedIds: number[]
): T[] {
  return allAudiences.filter((audience) => audience.id !== undefined && selectedIds.includes(audience.id))
}

export function resolveAudienceScopedStoreIds(
  includedAudiences: (Pick<Audience, 'store_ids'> | undefined)[]
): number[] | null {
  if (includedAudiences.length === 0) {
    return null
  }

  const storeIds = new Set<number>()

  for (const audience of includedAudiences) {
    // An audience the user can't see (undefined) has an unknown scope: don't restrict on it.
    if (!audience?.store_ids?.length) {
      return null
    }
    audience.store_ids.forEach((id) => storeIds.add(id))
  }

  return [...storeIds]
}

export function parseStoreIdsFromQuery(raw: unknown): number[] | undefined {
  if (raw === undefined || raw === null) {
    return undefined
  }

  const rawValues = (Array.isArray(raw) ? raw : [raw]).flatMap((value) => String(value).split(','))

  const storeIds = [...new Set(rawValues.map(Number).filter((value) => Number.isInteger(value) && value > 0))]

  return storeIds.length > 0 ? storeIds : undefined
}

export function resolveScopedStoreIds(selectedStoreIds: number[], totalStoreCount: number): number[] | null {
  return selectedStoreIds.length > 0 && selectedStoreIds.length < totalStoreCount ? selectedStoreIds : null
}

export function filterStoresByScope<T>(
  stores: T[],
  scopedStoreIds: number[] | null | undefined,
  getId: (store: T) => number
): T[] {
  return scopedStoreIds?.length ? stores.filter((store) => scopedStoreIds.includes(getId(store))) : stores
}

// An audience with no store means "every store": the picker shows that scope explicitly,
// so a global audience can't be read as an empty one and narrowed by mistake.
export function expandGlobalStoreScope(storeIds: number[], merchantStoreIds: number[]): number[] {
  return storeIds.length === 0 ? [...merchantStoreIds] : [...storeIds]
}

// Saving every store as a frozen list would leave out any store opened later: keep it global.
export function collapseFullStoreScope(storeIds: number[], merchantStoreIds: number[]): number[] {
  return merchantStoreIds.length > 0 && merchantStoreIds.every((id) => storeIds.includes(id)) ? [] : [...storeIds]
}
